import { useEffect, useState } from 'react'
import { Modal } from '@/components/lc'
import { cn } from '@/lib/utils'
import { web3formsEnabled, submitWeb3Form } from '@/lib/web3forms'

/* ==================================================================== */
/*  RequestModal — the "Price on request" enquiry popup.                 */
/*                                                                        */
/*  Mentors are priced per person, so instead of a fixed rate they show   */
/*  a "Price on request" CTA that opens this short form. Submitting        */
/*  POSTs to Web3Forms, which emails the enquiry to the team inbox — no    */
/*  mail client required, and the details are captured even if the         */
/*  visitor never had one. When the Web3Forms key isn't configured we      */
/*  fall back to a mailto: hand-off so the form still works.               */
/* ==================================================================== */

const EMAIL = 'info@lifecraft4u.com'
const WHATSAPP_LINK = 'https://wa.me/918796790333'

// Fixed light field so it stays legible in both the light (community) and dark
// (cosmos) modal themes — dark ink on a white input either way.
const inputCls =
  'w-full rounded-tile border border-black/10 bg-white px-4 py-3 text-[15px] text-night placeholder:text-night/45 outline-none transition-colors focus:border-lime'

type Status = 'idle' | 'sending' | 'sent' | 'error'

type Props = {
  open: boolean
  onClose: () => void
  /** What the request is about, e.g. a mentor or community name. */
  subject?: string
  theme?: 'light' | 'dark'
  /** 'mentor' = book a session enquiry (default); 'event' = express interest in an event. */
  kind?: 'mentor' | 'event'
}

export function RequestModal({ open, onClose, subject, theme = 'light', kind = 'mentor' }: Props) {
  const isEvent = kind === 'event'
  const [name, setName] = useState('')
  const [contact, setContact] = useState('')
  const [message, setMessage] = useState('')
  const [status, setStatus] = useState<Status>('idle')
  const [error, setError] = useState('')
  // Honeypot: a hidden field real users never see. Bots that auto-fill every
  // input will populate it, so a non-empty value means "drop silently".
  const [botField, setBotField] = useState('')

  // Reset the form whenever the modal is (re)opened for a fresh subject.
  useEffect(() => {
    if (open) {
      setName('')
      setContact('')
      setMessage('')
      setStatus('idle')
      setError('')
      setBotField('')
    }
  }, [open, subject])

  const title = isEvent
    ? (subject ? `Express your interest · ${subject}` : 'Express your interest')
    : (subject ? `Request a session · ${subject}` : 'Send a request')
  const sentSubject = isEvent
    ? (subject ? `Event interest: ${subject}` : 'Event interest')
    : (subject ? `1-1 request: ${subject}` : '1-1 consultation request')

  const mailtoFallback = () => {
    const body = [
      subject ? `Request about: ${subject}` : 'Consultation request',
      '',
      `Name: ${name}`,
      `Reach me on: ${contact}`,
      '',
      message || '(no message)',
    ].join('\n')
    const mail = `mailto:${EMAIL}?subject=${encodeURIComponent(sentSubject)}&body=${encodeURIComponent(body)}`
    window.location.href = mail
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Honeypot tripped → a bot filled the hidden field. Pretend success and
    // send nothing, so the bot gets no signal it was caught.
    if (botField) {
      setStatus('sent')
      return
    }

    // No key configured → keep the old mailto behaviour.
    if (!web3formsEnabled) {
      mailtoFallback()
      onClose()
      return
    }

    setStatus('sending')
    setError('')
    try {
      await submitWeb3Form({
        subject: sentSubject,
        from_name: name || 'LifeCraft4U enquiry',
        'Request about': subject ?? (isEvent ? 'Event interest' : 'General consultation'),
        Name: name,
        'Reach me on': contact,
        Message: message || '(no message)',
      })
      setStatus('sent')
    } catch (err) {
      setStatus('error')
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={status === 'sent' ? 'Request sent' : title} theme={theme}>
      {status === 'sent' ? (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-lime text-night" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m4.5 12.5 5 5 10-11" /></svg>
            </span>
            <p className="font-display text-2xl text-lc-ink">Thank you{name ? `, ${name.split(' ')[0]}` : ''}!</p>
          </div>
          <p className="text-[15px] leading-relaxed text-lc-mute">
            {isEvent
              ? <>Your interest{subject ? ` in ${subject}` : ''} has reached our team. We’ll share dates, details and how to register as soon as they’re confirmed.</>
              : <>Your request{subject ? ` about ${subject}` : ''} has reached our team. We’ll confirm the available slots and rate, and reply within 24 to 48 hours.</>}
          </p>
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <button
              onClick={onClose}
              className="rounded-pill bg-lime px-6 py-3 text-[15px] font-bold text-night transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100"
            >
              Done
            </button>
            {!isEvent && (
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noreferrer"
                className="lc-glass rounded-pill px-6 py-3 text-[15px] font-bold text-lc-ink transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100"
              >
                Message us on WhatsApp
              </a>
            )}
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          {/* honeypot — hidden from users, off the tab order; bots fill it */}
          <input
            type="text"
            name="company"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            value={botField}
            onChange={(e) => setBotField(e.target.value)}
            className="absolute left-[-9999px] h-0 w-0 opacity-0"
          />
          <p className="text-[14px] leading-relaxed text-lc-mute">
            {isEvent
              ? 'Tell us how to reach you and we’ll share dates, details and how to register as soon as they’re confirmed.'
              : 'Tell us how to reach you and our team will confirm this mentor’s available slots and rate.'}
          </p>
          <div>
            <label className="label !text-[11px] text-lc-mute">Your name</label>
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              className={cn(inputCls, 'mt-1.5')}
            />
          </div>
          <div>
            <label className="label !text-[11px] text-lc-mute">Email or phone</label>
            <input
              required
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              placeholder="How should we reach you?"
              className={cn(inputCls, 'mt-1.5')}
            />
          </div>
          <div>
            <label className="label !text-[11px] text-lc-mute">Anything to add? (optional)</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder="What you’re looking for, timing that suits you…"
              className={cn(inputCls, 'mt-1.5 resize-none')}
            />
          </div>
          {status === 'error' && (
            <p className="rounded-tile bg-red-500/10 px-4 py-2.5 text-[13px] text-red-600">
              {error} You can also{' '}
              <button type="button" onClick={mailtoFallback} className="font-semibold underline underline-offset-2">
                email us directly
              </button>
              .
            </p>
          )}
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <button
              type="submit"
              disabled={status === 'sending'}
              className="rounded-pill bg-lime px-6 py-3 text-[15px] font-bold text-night transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {status === 'sending' ? (isEvent ? 'Submitting…' : 'Sending…') : (isEvent ? 'Submit' : 'Send request')}
            </button>
            {!isEvent && (
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noreferrer"
                className="lc-glass rounded-pill px-6 py-3 text-[15px] font-bold text-lc-ink transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100"
              >
                Or WhatsApp us
              </a>
            )}
          </div>
          {!isEvent && (
            <p className="label !text-[11px] text-lc-mute">
              We reply within 24 to 48 hours
            </p>
          )}
        </form>
      )}
    </Modal>
  )
}
